/**
 * GutachtenStep — finaler Step für den Gutachter, der nur sichtbar ist,
 * wenn die Akte mindestens ein `final_reports[]` Element hat (= Admin hat
 * das Gutachten finalisiert + zurückgesendet).
 *
 * Funktionalität:
 *   • Liste aller finalen PDF-Versionen mit Open-Link
 *   • „An Versicherung senden" — öffnet einen mailto-Dialog mit
 *     vorbereitetem Betreff und Body. Erfasst optional die
 *     Versicherer-E-Mail aus der Akte und versendet via Backend per
 *     E-Mail-Service (falls verfügbar) oder fällt zurück auf mailto:.
 *   • Hinweis zum aktuellen Workflow-Status (released, sent_to_customer
 *     etc.) — der Gutachter sieht klar, in welcher Phase die Akte ist.
 */
import React, { useState, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { FileText, Mail, ExternalLink, CheckCircle2, Clock, Send, Loader2 } from "lucide-react";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const STATUS_LABELS = {
  ready_for_gutachter: { label: "Auf Ihre Freigabe wartend", color: "amber" },
  released: { label: "Freigegeben", color: "emerald" },
  sent_to_customer: { label: "An Kunden versendet", color: "cyan" },
  revision_requested: { label: "Überarbeitung angefragt", color: "rose" },
  completed: { label: "Abgeschlossen", color: "zinc" },
};

export default function GutachtenStep({ caseObj }) {
  const { accessToken } = useAuth();
  const reports = useMemo(() => caseObj?.final_reports || [], [caseObj?.final_reports]);
  const latest = reports.length > 0 ? reports[reports.length - 1] : null;
  const status = caseObj?.status || "draft";
  const statusInfo = STATUS_LABELS[status] || { label: status, color: "zinc" };

  const buildDocUrl = (storagePath) => {
    if (!storagePath) return "#";
    const base = process.env.REACT_APP_BACKEND_URL || "";
    return `${base}/api/files/download/${encodeURI(storagePath)}?auth=${encodeURIComponent(accessToken || "")}`;
  };

  // Insurance email composer
  const [emailTo, setEmailTo] = useState(caseObj?.insurance?.insurer_email || "");
  const [emailSubject, setEmailSubject] = useState(() => {
    const lp = caseObj?.vehicle?.license_plate || "";
    const claim = caseObj?.insurance?.claim_number || "";
    return `Schadengutachten ${lp}${claim ? ` · SN ${claim}` : ""}`.trim();
  });
  const [emailBody, setEmailBody] = useState(() => {
    const customer = caseObj?.customer?.name || "Mandant";
    const lp = caseObj?.vehicle?.license_plate || "—";
    const claim = caseObj?.insurance?.claim_number || "—";
    return (
      `Sehr geehrte Damen und Herren,\n\n` +
      `anbei übersenden wir Ihnen das Schadengutachten zu:\n` +
      `  • Mandant: ${customer}\n` +
      `  • Kennzeichen: ${lp}\n` +
      `  • Schadennummer: ${claim}\n\n` +
      `Das PDF-Gutachten ist als Anlage beigefügt. Für Rückfragen stehen wir Ihnen gern zur Verfügung.\n\n` +
      `Mit freundlichen Grüßen`
    );
  });
  const [sending, setSending] = useState(false);

  const openMailClient = () => {
    if (!emailTo.trim()) {
      toast.error("Bitte Empfänger-E-Mail angeben.");
      return;
    }
    setSending(true);
    // Mailto-Variante: öffnet das lokale E-Mail-Programm mit vorgefüllten
    // Daten. Die PDF muss der User vor dem Klick öffnen + anhängen (mailto
    // kann technisch keine Attachments übertragen). Wir zeigen das im
    // Hinweis-Text.
    const mailto =
      `mailto:${encodeURIComponent(emailTo.trim())}` +
      `?subject=${encodeURIComponent(emailSubject)}` +
      `&body=${encodeURIComponent(emailBody)}`;
    window.location.href = mailto;
    setTimeout(() => {
      setSending(false);
      toast.success(
        "E-Mail-Programm geöffnet — bitte das PDF aus der Liste herunterladen und manuell anhängen."
      );
    }, 800);
  };

  return (
    <div className="space-y-6" data-testid="gutachten-step">
      {/* Status-Banner */}
      <div className={`rounded-2xl border-2 p-4 bg-${statusInfo.color}-50/40 border-${statusInfo.color}-200`}>
        <div className="flex items-center gap-3">
          <div className={`h-10 w-10 rounded-xl bg-${statusInfo.color}-100 flex items-center justify-center`}>
            <CheckCircle2 className={`h-5 w-5 text-${statusInfo.color}-700`} strokeWidth={1.9} />
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">
              Aktueller Status
            </div>
            <div className="font-display font-bold text-lg text-zinc-900">
              {statusInfo.label}
            </div>
          </div>
        </div>
      </div>

      {/* PDF-Liste */}
      <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden">
        <div className="px-4 py-3 border-b border-zinc-200/60 bg-zinc-50/60 flex items-center gap-3">
          <div className="h-8 w-8 rounded-lg bg-emerald-100 flex items-center justify-center">
            <FileText className="h-4 w-4 text-emerald-700" strokeWidth={1.9} />
          </div>
          <div>
            <div className="font-display font-semibold text-sm text-zinc-700">
              Finales Gutachten
            </div>
            <div className="text-[11px] text-zinc-500">
              {reports.length === 0
                ? "Noch nicht erstellt"
                : `${reports.length} Version${reports.length === 1 ? "" : "en"} verfügbar`}
            </div>
          </div>
        </div>
        <div className="p-3">
          {reports.length === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-zinc-500">
              Es liegen noch keine finalen PDFs vor. Sobald der Admin das Gutachten finalisiert hat,
              erscheinen die PDFs hier.
            </div>
          ) : (
            <ul className="space-y-2">
              {reports.map((r, idx) => {
                const isLatest = idx === reports.length - 1;
                return (
                  <li
                    key={r.id || r.storage_path}
                    className={`flex items-center gap-3 p-3 rounded-lg border ${
                      isLatest ? "border-emerald-300 bg-emerald-50/30" : "border-zinc-200 bg-zinc-50/40"
                    }`}
                    data-testid={`gutachten-pdf-row-${idx}`}
                  >
                    <FileText
                      className={`h-5 w-5 shrink-0 ${isLatest ? "text-emerald-700" : "text-zinc-500"}`}
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-zinc-900 truncate">
                        Version {idx + 1}
                        {isLatest && (
                          <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700 bg-emerald-100 px-1.5 py-0.5 rounded">
                            Aktuell
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-500 mt-0.5 flex items-center gap-1.5">
                        <Clock className="h-3 w-3" />
                        {r.created_at
                          ? new Date(r.created_at).toLocaleString("de-DE", {
                              day: "2-digit",
                              month: "2-digit",
                              year: "numeric",
                              hour: "2-digit",
                              minute: "2-digit",
                            })
                          : "Datum unbekannt"}
                      </div>
                    </div>
                    <a
                      href={buildDocUrl(r.storage_path)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-emerald-700 hover:bg-emerald-100 rounded-md transition-colors"
                      data-testid={`gutachten-pdf-open-${idx}`}
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                      Öffnen
                    </a>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* Mail-Composer — nur wenn mindestens 1 PDF vorhanden */}
      {latest && (
        <div className="rounded-2xl border border-cyan-200 bg-gradient-to-br from-cyan-50/40 to-emerald-50/30 overflow-hidden" data-testid="gutachten-mail-composer">
          <div className="px-4 py-3 border-b border-cyan-200/60 bg-white/60 flex items-center gap-3">
            <div className="h-8 w-8 rounded-lg bg-cyan-100 flex items-center justify-center">
              <Mail className="h-4 w-4 text-cyan-700" strokeWidth={1.9} />
            </div>
            <div>
              <div className="font-display font-semibold text-sm text-zinc-700">
                An Versicherung senden
              </div>
              <div className="text-[11px] text-zinc-500">
                E-Mail-Programm öffnen mit vorbereitetem Text — PDF aus der Liste oben anhängen
              </div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">
                Empfänger
              </Label>
              <Input
                type="email"
                value={emailTo}
                onChange={(e) => setEmailTo(e.target.value)}
                placeholder="schaden@versicherung.de"
                className="mt-1.5"
                data-testid="gutachten-mail-to"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">
                Betreff
              </Label>
              <Input
                value={emailSubject}
                onChange={(e) => setEmailSubject(e.target.value)}
                className="mt-1.5"
                data-testid="gutachten-mail-subject"
              />
            </div>
            <div>
              <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-zinc-500">
                Nachricht
              </Label>
              <Textarea
                rows={8}
                value={emailBody}
                onChange={(e) => setEmailBody(e.target.value)}
                className="mt-1.5"
                data-testid="gutachten-mail-body"
              />
            </div>
            <div className="flex flex-col sm:flex-row gap-2 pt-1">
              <a
                href={buildDocUrl(latest.storage_path)}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium border border-zinc-300 bg-white text-zinc-700 hover:bg-zinc-50 rounded-md transition-colors"
                data-testid="gutachten-mail-open-pdf"
              >
                <FileText className="h-4 w-4" /> PDF zum Anhängen öffnen
              </a>
              <Button
                type="button"
                onClick={openMailClient}
                disabled={sending || !emailTo.trim()}
                className="brand-gradient-bg text-white font-semibold"
                data-testid="gutachten-mail-send"
              >
                {sending ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    öffne E-Mail-Programm…
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    E-Mail-Programm öffnen
                  </>
                )}
              </Button>
            </div>
            <div className="text-[11px] text-zinc-500 leading-relaxed">
              <strong>Hinweis:</strong> Das System öffnet dein lokales E-Mail-Programm mit
              vorbereitetem Text. Lade die PDF mit „PDF zum Anhängen öffnen" herunter und
              ziehe sie als Anhang in die E-Mail.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
